import PropTypes from 'prop-types'
import { cn } from '../../lib/cn'

/**
 * Checkbox component with label and error state
 *
 * @component
 * @example
 * <Checkbox
 *   label="Accept terms"
 *   checked={accepted}
 *   onChange={handleChange}
 * />
 */
export default function Checkbox({
  label,
  name,
  checked = false,
  onChange,
  error,
  disabled = false,
  className = '',
  ...props
}) {
  const checkboxId = name || label?.toLowerCase().replace(/\s+/g, '-')

  const checkboxClasses = cn(
    'h-4 w-4 rounded border-gray-300 transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    'disabled:cursor-not-allowed disabled:opacity-50',
    error
      ? 'text-red-600 focus:ring-red-500'
      : 'text-primary-600 focus:ring-primary-500',
    className
  )

  return (
    <div className="flex items-start">
      <input
        id={checkboxId}
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className={checkboxClasses}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${checkboxId}-error` : undefined}
        {...props}
      />
      {label && (
        <label
          htmlFor={checkboxId}
          className={cn(
            'ml-2 text-sm',
            disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'
          )}
        >
          {label}
        </label>
      )}
      {error && (
        <p
          id={`${checkboxId}-error`}
          className="ml-2 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  )
}

Checkbox.propTypes = {
  /** Checkbox label */
  label: PropTypes.string,
  /** Checkbox name attribute */
  name: PropTypes.string,
  /** Checked state */
  checked: PropTypes.bool,
  /** Change handler */
  onChange: PropTypes.func,
  /** Error message */
  error: PropTypes.string,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string
}
